<template>
  <nav aria-label="breadcrumb">
    <ol class="breadcrumb breadcrumb-chevron p-3 bg-body-tertiary rounded-3">
      <li class="breadcrumb-item"><RouterLink class="link-body-emphasis" to="/"><font-awesome-icon :icon="['fas', 'home']" /><span class="visually-hidden">Home</span></RouterLink></li>
      <li class="breadcrumb-item"><RouterLink class="link-body-emphasis fw-semibold text-decoration-none" :to="TranslationsService.i18nRoute({ name: 'label-printers' })">{{ $t('menu.label-printers') }}</RouterLink></li>
      <li class="breadcrumb-item active" aria-current="page">{{ $t('menu.label-printer-edit') }}</li>
    </ol>
  </nav>

  <div v-if="labelPrinter" class="tw-flex tw-flex-row tw-h-max tw-w-max">
    <table class="tw-px-5 tw-mx-5 tw-h-fit ">
      <tbody>
      <tr >
        <td colspan="2">
          <button class="btn btn-primary btn-lg d-block mb-3" type="submit" @click="save()">
            <font-awesome-icon :icon="['fas', 'floppy-disk']" />
            {{ $t('page.labelPrinter.form.confirm') }}
          </button>
        </td>
      </tr>
      <tr>
        <td class="tw-py-3 tw-px-3">
          <label class="tw-text-l tw-font-semibold" for="code">{{ $t('field.generic.code') }}</label>
        </td>
        <td class="tw-py-3 tw-px-3">
          <input v-model="labelPrinter.code" type="text" class="form-control" size="25" maxlength="20" id="code" ref="code" aria-describedby="codeHelp">
        </td>
      </tr>
      <tr>
        <td class="tw-py-3 tw-px-3">
          <label class="tw-text-l tw-font-semibold" for="name">{{ $t('page.labelPrinter.form.address') }}</label>
        </td>
        <td class="tw-py-3 tw-px-3">
          <input v-model="labelPrinter.address" type="text" class="form-control" size="25" maxlength="100" id="address" ref="address" aria-describedby="addressHelp">
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <span >{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">

import {useLabelPrintersStore, useSessionStore, useSitesStore,} from "@/stores";
import {computed, onMounted, ref} from "vue";
import TranslationsService from "@/services/translations.ts";
import {toast, type ToastOptions} from "vue3-toastify";
import { useI18n } from "vue-i18n";
import {router} from "@/services";
import ILabelPrinterUpsertRequest from "@/models/requests/ILabelPrinterUpsertRequest";
import {storeToRefs} from "pinia";
import LabelPrinter from "@/models/frontend/LabelPrinter";

const props = defineProps({
  printerId: { type: Number, required: false }
});

const i18n = useI18n();

const labelPrinterStore = useLabelPrintersStore();
const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);
const siteStore = useSitesStore();
const { sites } = storeToRefs(siteStore);
const currentSite = computed(() => {
  return sites.value.find(x => x.id == selectedSiteId.value);
});

const labelPrinter = ref<LabelPrinter | undefined>();
const isSaving = ref(false);

onMounted(async () => {
  if (props.printerId) {
    labelPrinter.value = labelPrinterStore.getById(props.printerId);
  }

  labelPrinter.value = labelPrinter.value ?? {} as LabelPrinter
});

const save = async () => {
  if (!labelPrinter.value) {
    return;
  }

  isSaving.value = true;

  const request: ILabelPrinterUpsertRequest = {
    siteCode: labelPrinter.value.site?.code
      ?? currentSite.value!.code,
    printerCode: labelPrinter.value.code,
    address: labelPrinter.value.address
  }

  if (props.printerId) {
    await labelPrinterStore.update(labelPrinter.value.id, request);
  } else {
    await labelPrinterStore.create(request);
  }

  if (labelPrinterStore.error) {
    toast.error(i18n.t('page.labelPrinter.saveError', { error: labelPrinterStore.error }), { theme: 'colored' });
    isSaving.value = false;
  } else {
    toast.info(i18n.t('page.labelPrinter.saved'), { theme: 'colored', autoClose: 800 } as ToastOptions);

    setTimeout(() => {
      router.push({ name: 'label-printers' });
      isSaving.value = false;
    }, 1000);
  }
};
</script>
